import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { ProtectedRoute } from '@/lib/auth';
import { CapabilityRoute } from '@/components/billing';
import { MarketingPopupGate } from '@/components/marketing';
import type { Role } from '@/lib/api/types';
import { AppShell } from './shell/AppShell';
import { PageFallback } from './PageFallback';

// Lazy page chunks — each screen is split so the initial bundle stays lean.
const LandingPage = lazy(() => import('@/pages/Landing/LandingPage'));
const LoginPage = lazy(() => import('@/pages/Login/LoginPage'));
const SignUpPage = lazy(() => import('@/pages/Login/SignUpPage'));
const ResetPasswordPage = lazy(() => import('@/pages/Login/ResetPasswordPage'));
const AuthCallbackPage = lazy(() => import('@/pages/Login/AuthCallbackPage'));
const PrivacyPolicy = lazy(() => import('@/pages/Legal/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('@/pages/Legal/TermsOfUse'));
const PerfilPage = lazy(() => import('@/pages/Perfis/PerfilPage'));
const PlanosPage = lazy(() => import('@/pages/Planos/PlanosPage'));
const FaqPage = lazy(() => import('@/pages/Faq/FaqPage'));
const CheckoutPage = lazy(() => import('@/pages/Assinatura/CheckoutPage'));
const CancelarPage = lazy(() => import('@/pages/Assinatura/CancelarPage'));
const DashboardPage = lazy(() => import('@/pages/Dashboard/DashboardPage'));
const KnowledgePage = lazy(() => import('@/pages/Knowledge/KnowledgePage'));
const ChatPage = lazy(() => import('@/pages/Knowledge/ChatPage'));
const ConversasPage = lazy(() => import('@/pages/Conversas/ConversasPage'));
const DecksPage = lazy(() => import('@/pages/Decks/DecksPage'));
const AdminPage = lazy(() => import('@/pages/Admin/AdminPage'));
const SettingsPage = lazy(() => import('@/pages/Settings/SettingsPage'));
const AccountPage = lazy(() => import('@/pages/Account/AccountPage'));
const AdvancedSettingsPage = lazy(() => import('@/pages/AdvancedSettings/AdvancedSettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFound/NotFoundPage'));

/** Wrap a lazy element in Suspense with the content-shaped fallback. */
function page(element: ReactNode): ReactNode {
  return (
    <Suspense fallback={<PageFallback />}>
      {element}
      <MarketingPopupGate />
    </Suspense>
  );
}

/** Gate a route on a minimum role, then render it inside Suspense. */
function gated(element: ReactNode, requireRole?: Role, requiresCapability?: string): ReactNode {
  let inner = page(element);
  // O portão comercial fica DENTRO do de sessão: quem não entrou vai para o
  // login; quem entrou sem a capability vai para /planos, nunca em loop.
  if (requiresCapability) {
    inner = <CapabilityRoute capability={requiresCapability}>{inner}</CapabilityRoute>;
  }
  return requireRole ? <ProtectedRoute requireRole={requireRole}>{inner}</ProtectedRoute> : inner;
}

/** Keep chat-era Knowledge bookmarks working after the screen split. */
function KnowledgeRoute(): JSX.Element {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (params.has('conversa') || params.has('interacao')) {
    return <Navigate to={`/app/chat${search}`} replace />;
  }
  return <>{gated(<KnowledgePage />)}</>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Suspense fallback={<PageFallback />}>
        <LandingPage />
        <MarketingPopupGate />
      </Suspense>
    ),
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageFallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  { path: '/cadastro', element: page(<SignUpPage />) },
  { path: '/recuperar-senha', element: page(<ResetPasswordPage />) },
  { path: '/redefinir-senha', element: page(<ResetPasswordPage />) },
  {
    // Public by design: the Cognito Hosted UI redirects here with the OAuth
    // code — gating it behind ProtectedRoute would deadlock the login.
    path: '/auth/callback',
    element: page(<AuthCallbackPage />),
  },
  {
    path: '/privacidade',
    element: (
      <Suspense fallback={<PageFallback />}>
        <PrivacyPolicy />
      </Suspense>
    ),
  },
  { path: '/privacy', element: <Navigate to="/privacidade" replace /> },
  {
    path: '/termos',
    element: (
      <Suspense fallback={<PageFallback />}>
        <TermsOfUse />
      </Suspense>
    ),
  },
  { path: '/terms', element: <Navigate to="/termos" replace /> },
  { path: '/perfis/:slug', element: page(<PerfilPage />) },
  { path: '/planos', element: page(<PlanosPage />) },
  { path: '/faq', element: page(<FaqPage />) },
  {
    // O fluxo comercial é autenticado mas fica FORA do /app: páginas standalone,
    // sem o AppShell, como o site público — a compra não é o console. Sem o /app
    // em volta, o portão de sessão é destas rotas: quem não entrou vai ao /login
    // com o destino em state.from, em vez de cair no 401 da assinatura.
    path: '/assinatura/checkout',
    element: <ProtectedRoute>{page(<CheckoutPage />)}</ProtectedRoute>,
  },
  {
    path: '/assinatura/cancelar',
    element: <ProtectedRoute>{page(<CancelarPage />)}</ProtectedRoute>,
  },
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: gated(<DashboardPage />) },
      { path: 'conhecimento', element: <KnowledgeRoute /> },
      { path: 'chat', element: gated(<ChatPage />) },
      { path: 'conversas', element: gated(<ConversasPage />) },
      { path: 'apresentacoes', element: gated(<DecksPage />, undefined, 'decks') },
      // Treinamentos folded into Conhecimento (enrich mode in the upload
      // dialog); keep the old URL working as a redirect.
      { path: 'treinamentos', element: <Navigate to="/app/conhecimento" replace /> },
      { path: 'qa', element: <Navigate to="/app/conhecimento" replace /> },
      { path: 'administracao', element: gated(<AdminPage />, 'admin') },
      { path: 'configuracoes', element: gated(<SettingsPage />) },
      { path: 'configuracoes-avancadas', element: gated(<AdvancedSettingsPage />, 'admin') },
      { path: 'configuracoes-avancadas/:section', element: gated(<AdvancedSettingsPage />, 'admin') },
      { path: 'conta', element: gated(<AccountPage />) },
      { path: 'conta/:section', element: gated(<AccountPage />) },
      { path: '*', element: page(<NotFoundPage />) },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
